import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  useMediaQuery,
  useTheme,
  Paper,
  Button,
  Divider,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  alpha,
  List,
  ListItem,
  ListItemText,
  Fade,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  ArrowUpward as ArrowUpwardIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { toggleTheme } from '../../store/actions/themeActions';
import { setUserSettingsAction } from '../../store/actions/profile';
import { updateUserSettings, getBankAccountOtp } from '../../services/backendAPI';
import { formatProduct } from '../../utils/utilfunction';
import AddBankModal from '../PayIn/AddBankModal';
import SettingsDialog from './Settings/SettingsDialog';
import PreferencesTab from './Settings/PreferencesTab';
import WithdrawModal from './WithdrawModal';
import useBalance from '../Portfolio/Positions/useBalance';
import { updateCalculatedFunds } from '../../store/actionCreators/fundsActions';

// Custom tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  const theme = useTheme();

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{ p: { xs: 2, sm: 3 }, transition: 'opacity 300ms', animation: value === index ? 'fadeIn 300ms ease-in-out' : 'none', '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Data row component for account details
const DataRow = ({ label, value, divider = true }) => {
  const theme = useTheme();
  return (
    <>
      <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
        <TableCell 
          component="th" 
          scope="row" 
      sx={{ 
            color: theme.palette.text.secondary,
            fontWeight: 600,
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            width: '40%',
            py: { xs: 1.5, sm: 2 },
            borderBottom: divider ? `1px solid ${theme.palette.divider}` : 'none'
          }}
        >
          {label}
        </TableCell>
        <TableCell 
          sx={{ 
            color: theme.palette.text.primary,
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            py: { xs: 1.5, sm: 2 },
            borderBottom: divider ? `1px solid ${theme.palette.divider}` : 'none'
          }}
        >
          {value || 'Not provided'}
        </TableCell>
      </TableRow>
    </>
  );
};

// Bank account card component
const BankAccountCard = ({ account, onEdit, onDelete }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <Card 
      sx={{ 
        mb: 3, 
        borderRadius: '12px',
        boxShadow: isHovered 
          ? '0 10px 25px rgba(0,0,0,0.1)' 
          : '0 4px 12px rgba(0,0,0,0.05)',
        transition: 'all 0.3s ease',
        overflow: 'hidden',
        position: 'relative',
        transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Border top accent */}
      <Box 
        sx={{ 
          height: '4px',
          width: '100%',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
        }}
      />
      
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          mb: 2.5,
          pb: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: alpha(theme.palette.primary.main, 0.1), 
                color: theme.palette.primary.main,
                width: 40,
                height: 40,
              }}
            >
              <BankIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                fontSize: '1.1rem',
                color: theme.palette.text.primary,
              }}>
                {account.bank_name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                {account.account_type} Account
              </Typography>
      </Box>
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontSize: '0.7rem',
                fontWeight: 600,
                mb: 0.5,
                display: 'block',
                textTransform: 'uppercase',
              }}>
                Account Number
              </Typography>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 1.5,
                p: 1.5,
                mb: 2
              }}>
                <Typography sx={{ 
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}>
                  {account.bank_account_number}
                </Typography>
                <IconButton 
                  size="small" 
          sx={{ 
                    opacity: 0.6,
                    padding: '4px'
                  }}
                  onClick={() => {/* Copy to clipboard function */}}
                >
                  <Box component="span" sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ 
                      display: 'inline-block', 
                      width: 12, 
                      height: 12, 
                      border: `1.5px solid ${alpha(theme.palette.text.secondary, 0.8)}`, 
                      borderRadius: 0.5,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -4,
                        left: -4,
                        width: 12,
                        height: 12,
                        border: `1.5px solid ${alpha(theme.palette.text.secondary, 0.8)}`,
                        borderRadius: 0.5,
                        backgroundColor: alpha(theme.palette.background.paper, 0.8)
                      }
                    }} />
                  </Box>
                </IconButton>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ 
            fontSize: '0.7rem', 
                fontWeight: 600,
                mb: 0.5,
                display: 'block',
            textTransform: 'uppercase',
              }}>
                IFSC Code
              </Typography>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: alpha(theme.palette.background.default, 0.5),
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                borderRadius: 1.5,
                p: 1.5,
              }}>
                <Typography sx={{ 
                  fontSize: '0.9rem',
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                }}>
                  {account.ifsc_code}
                </Typography>
                <IconButton 
                  size="small" 
                  sx={{ 
                    opacity: 0.6,
                    padding: '4px'
                  }}
                  onClick={() => {/* Copy to clipboard function */}}
                >
                  <Box component="span" sx={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ 
                      display: 'inline-block', 
                      width: 12, 
                      height: 12, 
                      border: `1.5px solid ${alpha(theme.palette.text.secondary, 0.8)}`, 
                      borderRadius: 0.5,
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -4,
                        left: -4,
                        width: 12,
                        height: 12,
                        border: `1.5px solid ${alpha(theme.palette.text.secondary, 0.8)}`,
                        borderRadius: 0.5,
                        backgroundColor: alpha(theme.palette.background.paper, 0.8)
                      }
                    }} />
                  </Box>
                </IconButton>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: '0.7rem',
              fontWeight: 600,
            mb: 0.5,
              display: 'block',
              textTransform: 'uppercase',
            }}>
              Branch Details
            </Typography>
            <Box sx={{ 
              backgroundColor: alpha(theme.palette.background.default, 0.5),
              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: 1.5,
              p: 1.5,
              height: 'calc(100% - 22px)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                <LocationIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary, mt: 0.3 }} />
                <Typography sx={{ 
                  fontSize: '0.9rem', 
                  fontWeight: 600, 
                  color: theme.palette.text.primary 
                }}>
                  {account.bank_branch_name}
                </Typography>
              </Box>
              <Typography sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.8rem',
                pl: 3.5
              }}>
                {account.bank_city}, {account.bank_state}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

// Preference toggle component
const PreferenceToggle = ({ label, checked, onChange, description, icon }) => {
  const theme = useTheme();
  return (
    <Box sx={{ 
      mb: 4, 
      px: 3, 
      py: 2, 
      backgroundColor: alpha(theme.palette.background.paper, 0.6),
      borderRadius: 2,
      border: `1px solid ${alpha(checked ? theme.palette.primary.main : theme.palette.divider, checked ? 0.3 : 0.1)}`,
      transition: 'all 0.3s ease',
      boxShadow: checked 
        ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}` 
        : '0 2px 8px rgba(0,0,0,0.03)',
      position: 'relative',
      overflow: 'hidden',
      '&:hover': {
        boxShadow: checked 
          ? `0 6px 16px ${alpha(theme.palette.primary.main, 0.2)}` 
          : '0 4px 12px rgba(0,0,0,0.06)',
        transform: 'translateY(-2px)',
      },
    }}>
      {/* Background gradient for checked state */}
      {checked && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: theme.palette.primary.main,
          opacity: 0.03,
          zIndex: 0,
        }} />
      )}
      
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 1.5,
        zIndex: 1,
        position: 'relative',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {icon && (
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: checked 
                  ? alpha(theme.palette.primary.main, 0.1) 
                  : alpha(theme.palette.action.hover, 0.1),
                color: checked ? theme.palette.primary.main : theme.palette.text.secondary,
                transition: 'all 0.3s ease',
              }}
            >
              {icon}
            </Avatar>
          )}
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              color: checked ? theme.palette.primary.main : theme.palette.text.primary,
              transition: 'all 0.3s ease',
          }}
        >
          {label}
        </Typography>
        </Box>
        
        <Switch 
          checked={checked} 
          onChange={onChange} 
          color="primary" 
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              transform: 'translateX(16px)',
              color: theme.palette.common.white,
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 0.8,
            }
          }}
        />
      </Box>
      
      {description && (
        <Typography 
          variant="body2" 
          color={checked ? 'text.primary' : 'text.secondary'}
          sx={{ 
            fontSize: { xs: '0.8rem', sm: '0.85rem' },
            ml: icon ? 7 : 0,
            position: 'relative',
            zIndex: 1,
            opacity: checked ? 0.9 : 0.7,
            transition: 'all 0.3s ease',
          }}
        >
          {description}
        </Typography>
      )}
      </Box>
  );
};

// Animated scroll up button for mobile view
const ScrollToTopButton = () => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <Fade in={isVisible}>
      <Box
        onClick={scrollToTop}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1000,
          width: 44,
          height: 44,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          backgroundColor: alpha(theme.palette.primary.main, 0.9),
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
          '&:hover': {
            backgroundColor: theme.palette.primary.main,
            transform: 'translateY(-3px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
          },
          '&:active': {
            transform: 'translateY(0)',
          }
        }}
      >
        <ArrowUpwardIcon sx={{ color: 'white', fontSize: '1.2rem' }} />
    </Box>
    </Fade>
  );
};

const Profile = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const themeMode = useSelector(state => state.theme.mode);
  const { userData, userSettings, isUpdating } = useSelector(state => state.profile);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Get global funds data
  const balanceData = useBalance();
  
  const [tabValue, setTabValue] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);

  // Settings dialog states
  const [openSettingsDialog, setOpenSettingsDialog] = useState(false);
  const [orderType, setOrderType] = useState('');
  const [productType, setProductType] = useState('');
  const [settingsUpdated, setSettingsUpdated] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Add this state for the AddBankModal
  const [isAddBankModalOpen, setIsAddBankModalOpen] = useState(false);

  // Add this state at the top of the Profile component
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);

  useEffect(() => {
    const hasVisitedProfile = localStorage.getItem('hasVisitedProfile');
    if (hasVisitedProfile) {
      setShowWelcome(false);
    } else {
      const timer = setTimeout(() => {
        setShowWelcome(false);
        localStorage.setItem('hasVisitedProfile', 'true');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (userSettings) {
      setOrderType(userSettings.order_type);
      setProductType(userSettings.product_type);
    }
  }, [userSettings]);

  useEffect(() => {
    // Fetch the latest calculated funds data when profile loads
    dispatch(updateCalculatedFunds());
  }, [dispatch]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleThemeToggle = () => {
    dispatch(toggleTheme());
  };

  const handleSaveSettings = async () => {
    const settingsUpdatePayload = {
      order_type: orderType,
      product_type: productType,
      client_id: userData?.client_id || userSettings?.client_id
    };

    try {
      dispatch({ type: 'UPDATE_USER_SETTINGS_START' });
      await updateUserSettings(settingsUpdatePayload);
      
      dispatch(setUserSettingsAction({
        ...userSettings,
        ...settingsUpdatePayload
      }));
      
      dispatch({ type: 'UPDATE_USER_SETTINGS_SUCCESS' });
      setSettingsUpdated(true);
      setTimeout(() => setSettingsUpdated(false), 3000);
      setOpenSettingsDialog(false);
      if (updateError) setUpdateError(null);
    } catch (error) {
      console.error('Error updating settings:', error);
      dispatch({ type: 'UPDATE_USER_SETTINGS_FAILURE', payload: error.message });
      setUpdateError(error.message || 'Failed to update settings');
    }
  };

  // Add this handler function
  const handleAddBankAccount = () => {
    setIsAddBankModalOpen(true);
  };

  // Format currency for display with option to show + for positive values
  const formatCurrency = (value, showSign = false) => {
    const numValue = parseFloat(value);
    const isPositive = numValue > 0;
    const prefix = showSign && isPositive ? '+' : '';
    return `${prefix}₹${Math.abs(numValue).toFixed(2)}`;
  };

  if (!userData) {
    return null;
  }

  return (
    <Box sx={{ 
      width: '100%', 
      backgroundColor: theme.palette.background.default,
      height: '100%',
      overflow: 'auto',
      position: 'relative',
    }}>
      {/* Welcome Message */}
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={showWelcome}
        onClose={() => setShowWelcome(false)}
        sx={{ top: { xs: 16, sm: 24 } }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setShowWelcome(false)}
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            alignItems: 'center',
          }}
          icon={<PersonIcon />}
        >
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
              Welcome to your profile, {userData?.name?.split(' ')[0]}!
            </Typography>
            <Typography variant="body2">
              Manage your account details and preferences in one place.
            </Typography>
          </Box>
        </Alert>
      </Snackbar>

      {/* Theme Toggle */}
      <Box sx={{
        position: 'absolute',
        top: { xs: 8, sm: 16 },
        right: { xs: 8, sm: 16 },
        zIndex: 10,
        backgroundColor: alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(8px)',
        borderRadius: 2,
        padding: { xs: '6px 12px', sm: '8px 16px' },
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
      }}>
        <FormControlLabel
          control={
            <Switch
              checked={themeMode === 'dark'}
              onChange={handleThemeToggle}
              color="primary"
              size={isMobile ? "small" : "medium"}
            />
          }
          label={`${themeMode === 'dark' ? 'Dark' : 'Light'}`}
          labelPlacement="start"
          sx={{ m: 0 }}
        />
      </Box>

      {/* Cover Photo Section */}
      <Box 
        sx={{ 
          height: { xs: '220px', sm: '280px', md: '300px' },
          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${alpha(theme.palette.primary.main, 0.8)})`,
          position: 'relative',
          mb: { xs: 8, sm: 10 },
          width: '100%',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            height: '30%',
            background: `linear-gradient(to top, ${alpha(theme.palette.background.default, 0.2)}, transparent)`,
          },
          // Add animated gradient effect
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(45deg, transparent 30%, ${alpha(theme.palette.primary.light, 0.1)} 50%, transparent 70%)`,
            backgroundSize: '400% 400%',
            animation: 'gradientShift 8s ease infinite',
          },
          '@keyframes gradientShift': {
            '0%': {
              backgroundPosition: '0% 50%'
            },
            '50%': {
              backgroundPosition: '100% 50%'
            },
            '100%': {
              backgroundPosition: '0% 50%'
            }
          }
        }}
      >
        {/* Pattern overlay */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.05,
            backgroundImage: 'radial-gradient(circle at 20px 20px, white 2px, transparent 0)',
            backgroundSize: '40px 40px',
          }} 
        />
        
        {/* Avatar and Name Container */}
        <Container maxWidth={false} sx={{ width: '100%', height: '100%', position: 'relative' }}>
          <Box sx={{ 
            position: 'absolute',
            bottom: '-20px',
            left: { xs: '16px', sm: '32px' },
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 2,
          }}>
            <Box sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '-6px',
                left: '-6px',
                right: '-6px',
                bottom: '-6px',
                borderRadius: '50%',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                opacity: 0.7,
                zIndex: 0,
                animation: 'pulse 2s ease-in-out infinite alternate',
              },
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 0.7
                },
                '100%': {
                  transform: 'scale(1.05)',
                  opacity: 0.9
                }
              }
          }}>
            <Avatar
              sx={{
                  width: { xs: 80, sm: 100, md: 140 },
                  height: { xs: 80, sm: 100, md: 140 },
                border: `4px solid ${theme.palette.background.paper}`,
                bgcolor: theme.palette.background.paper,
                color: theme.palette.primary.main,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '3.5rem' },
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                  position: 'relative',
                  zIndex: 1,
              }}
            >
              {userData?.name?.charAt(0).toUpperCase() || 'U'}
            </Avatar>
            </Box>

            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              alignItems: 'flex-start',
            }}>
              <Typography 
                variant={isMobile ? "h6" : "h4"}
                sx={{ 
                  fontWeight: 700,
                  color: 'white',
                  textShadow: '0px 2px 4px rgba(0,0,0,0.3)',
                  lineHeight: { xs: 1.1, sm: 1.2, md: 1.4 },
                  fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' },
                }}
              >
                {userData?.name || 'User Profile'}
              </Typography>
              <Typography 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)',
                  textShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                  fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Box component="span" sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  px: 1.5, 
                  py: 0.5, 
                  borderRadius: 5,
                  fontWeight: 500,
                  backdropFilter: 'blur(4px)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}>
                {userData?.client_id || 'Client ID'}
                </Box>
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Tabs Section */}
      <Box sx={{ width: '100%', px: 0 }}>
        <Paper elevation={0} sx={{ 
          borderRadius: 0,
          overflow: 'hidden',
          mb: 4,
          width: '100%',
          boxShadow: {
            xs: 'none',
            sm: '0 1px 3px rgba(0,0,0,0.1)',
          },
          position: 'sticky',
          top: 0,
          zIndex: 5,
          backdropFilter: 'blur(10px)',
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
        }}>
          <Box sx={{ width: '100%' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ 
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? alpha(theme.palette.grey[900], 0.7) 
                  : alpha(theme.palette.grey[50], 0.7),
                width: '100%',
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: 3,
                },
              }}
            >
              <Tab 
                label="Account Details" 
                icon={<PersonIcon />} 
                iconPosition="start"
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  py: { xs: 2, sm: 2.5 },
                  width: '50%',
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  },
                  '& .MuiSvgIcon-root': {
                    transition: 'all 0.2s ease',
                  },
                  '&.Mui-selected .MuiSvgIcon-root': {
                    color: theme.palette.primary.main,
                    transform: 'scale(1.2)',
                  }
                }}
              />
              <Tab 
                label="Preferences" 
                icon={<SettingsIcon />} 
                iconPosition="start"
                sx={{ 
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: { xs: '0.8rem', sm: '0.9rem' },
                  py: { xs: 2, sm: 2.5 },
                  width: '50%',
                  transition: 'all 0.2s ease',
                  '&.Mui-selected': {
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                  },
                  '& .MuiSvgIcon-root': {
                    transition: 'all 0.2s ease',
                  },
                  '&.Mui-selected .MuiSvgIcon-root': {
                    color: theme.palette.primary.main,
                    transform: 'scale(1.2)',
                  }
                }}
              />
            </Tabs>
          </Box>

          {/* Account Details Tab */}
          <TabPanel value={tabValue} index={0}>
            <Container maxWidth="lg">
              {/* Personal Information Card */}
              <Card sx={{ 
                mb: 4, 
                borderRadius: { xs: 2, sm: 3 }, 
                overflow: 'hidden',
                boxShadow: '0 6px 16px rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                }
              }}>
            <Box sx={{
                  px: { xs: 2, sm: 3 }, 
                  py: 2, 
              display: 'flex',
                  alignItems: 'center',
                  background: theme.palette.mode === 'dark' 
                    ? `linear-gradient(45deg, ${alpha(theme.palette.grey[900], 0.9)}, ${alpha(theme.palette.grey[800], 0.9)})` 
                    : `linear-gradient(45deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
                  borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      mr: 1.5
                    }}
                  >
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                    Personal Information
                  </Typography>
                </Box>
                
                <Box sx={{ p: { xs: 1, sm: 2 } }}>
                  <TableContainer component={Box} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                    <Table size={isMobile ? "small" : "medium"}>
                      <TableBody>
                        <DataRow label="Client ID" value={userData.client_id} />
                        <DataRow label="Name" value={userData.name} />
                        <DataRow label="Email" value={userData.email_id} />
                        <DataRow label="Phone" value={userData.phone_number} />
                        {/* <DataRow label="PAN Number" value={userData.pan_number} divider={false} /> */}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Card>

              {/* Add Bank Account Section */}
              <Box sx={{ 
                mt: 4,
                p: 4,
                borderRadius: 3,
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  borderColor: theme.palette.primary.main,
                }
              }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    mb: 2
                  }}
                >
                  <BankIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Add Bank Account
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 3, maxWidth: 400 }}
                >
                  Add your bank account details for seamless transactions and fund transfers
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddBankAccount}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                    }
                  }}
                >
                  Add Bank Account
                </Button>
              </Box>

              {/* Withdraw Funds Section */}
              {/* <Box sx={{ 
                mt: 4,
                p: 4,
                borderRadius: 3,
                border: `1px dashed ${alpha(theme.palette.success.main, 0.3)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, 0.8),
                  borderColor: theme.palette.success.main,
                }
              }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    mb: 2
                  }}
                >
                  <AccountBalanceWalletIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  Withdraw Funds
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 3, maxWidth: 400 }}
                >
                  Withdraw funds to your registered bank account quickly and securely
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AccountBalanceWalletIcon />}
                  onClick={() => setIsWithdrawModalOpen(true)}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.2)}`,
                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      boxShadow: `0 6px 16px ${alpha(theme.palette.success.main, 0.3)}`,
                    }
                  }}
                >
                  Withdraw Funds
                </Button>
              </Box> */}
            </Container>
          </TabPanel>

          {/* Preferences Tab */}
          <TabPanel value={tabValue} index={1}>
            <PreferencesTab 
              userSettings={userSettings || {}}
              handleOpenSettingsDialog={() => setOpenSettingsDialog(true)}
            />
          </TabPanel>
        </Paper>
      </Box>

      {/* Settings Dialog */}
      <SettingsDialog
        open={openSettingsDialog}
        onClose={() => setOpenSettingsDialog(false)}
        orderType={orderType}
        setOrderType={setOrderType}
        productType={productType}
        setProductType={setProductType}
        onSave={handleSaveSettings}
        isUpdating={isUpdating}
        updateError={updateError}
        theme={theme}
      />

      {/* Settings Updated Snackbar */}
      <Snackbar
        open={settingsUpdated}
        autoHideDuration={3000}
        onClose={() => setSettingsUpdated(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSettingsUpdated(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Settings updated successfully
        </Alert>
      </Snackbar>

      {/* AddBankModal */}
      <AddBankModal
        open={isAddBankModalOpen}
        onClose={() => setIsAddBankModalOpen(false)}
        onSubmit={(data) => {
          // Handle the bank account submission here
          setIsAddBankModalOpen(false);
        }}
      />

      {/* WithdrawModal */}
      <WithdrawModal
        open={isWithdrawModalOpen}
        onClose={() => setIsWithdrawModalOpen(false)}
        bankAccounts={userData?.bank_accounts || []}
      />
    </Box>
  );
};

export default Profile;